import { daysBetween, fromIso, iso, monthStart, quarterStart, shiftDate, shiftMonths, startOfWeek } from '../format/dates'
import { OWNER_STAFF_ID } from './salonDays'
import type { PaymentMethod, PaymentStatus } from '../types/requests'
import type { ProductKind } from '../types/catalog'
import type {
  ChannelBreakdown,
  MoneyMethod,
  PerformerBreakdown,
  OrderFulfilment,
  ReceivedMethod,
  RefundLine,
  SalesChannel,
  SalesGroupBy,
  SalesReport,
  SalesTotals,
} from '../types/adminRequests'

/**
 * Omzet en terugbetalen (BEHEER-PLAN §3.1 en §3.5, BOEKHOUDING §1) — één set regels voor de demo, de schermen
 * en de echte server (LARAVEL.md §9.6). Alle bedragen in hele centen.
 *
 * - Omzet = betaalde orders op de datum van betaling, exclusief btw per regel:
 *   bedrag × 100 / (100 + tarief), afgerond per regel op hele centen.
 * - Een terugbetaling is een negatieve regel op de datum van terugbetalen. Een periode die al
 *   naar de boekhouder ging, verandert zo nooit meer.
 * - Verzendkosten zijn een eigen regel, met hun eigen tarief.
 * - Cadeaubonnen zijn geen omzet (btw 0, meervoudige voucher): een aparte regel.
 * - Een ingewisselde cadeaubon is geen omzet bovenop de producten: de producten tellen gewoon
 *   (met hun btw), alleen het geld kwam binnen als "cadeaubon" in plaats van via de betaalmethode.
 */

export const DEFAULT_VAT_RATE = 21
export const GIFT_CARD_VAT_RATE = 0
/** Verzendkosten volgen het tarief van de producten. */
export const SHIPPING_VAT_RATE = DEFAULT_VAT_RATE

/**
 * Standaardtarief per soort product. Een `Record` over alle soorten: komt er een soort bij, dan
 * dwingt de typecheck hier een keuze af in plaats van stil 21% te geven.
 * - cadeaubon: 0 (meervoudige voucher, btw pas bij inwisselen);
 * - kuur: 21 bij verkoop over het hele bedrag (één doel, vaste behandeling; K5).
 */
const VAT_BY_KIND: Record<ProductKind, number> = {
  skincare: DEFAULT_VAT_RATE,
  giftcard: GIFT_CARD_VAT_RATE,
  kuur: DEFAULT_VAT_RATE,
}

/** Het btw-tarief van een product: het eigen tarief, anders het standaardtarief van de soort. */
export const vatRateOf = (p: { vatRate?: number; kind?: ProductKind }): number => p.vatRate ?? VAT_BY_KIND[p.kind ?? 'skincare']

/** Bedrag incl. btw → excl. btw, afgerond op hele centen. Negatief (terugbetaling) rondt symmetrisch. */
export function exVat(grossCents: number, vatRate: number): number {
  const net = Math.round((Math.abs(grossCents) * 100) / (100 + vatRate))
  return grossCents < 0 ? -net : net
}

/**
 * Verdeelt een bedrag naar verhouding over delen, zonder dat er een cent verdwijnt
 * (grootste rest krijgt de overgebleven centen). Som van de uitkomst = `amount`.
 */
export function allocate(amount: number, weights: number[]): number[] {
  if (weights.length === 0) return []
  const total = weights.reduce((s, w) => s + w, 0)
  if (total <= 0) return weights.map((_, i) => (i === 0 ? amount : 0))
  const raw = weights.map((w) => (amount * w) / total)
  const out = raw.map((r) => Math.floor(r))
  let rest = amount - out.reduce((s, v) => s + v, 0)
  const byRemainder = raw.map((r, i) => ({ r: r - Math.floor(r), i })).sort((a, b) => b.r - a.r)
  for (let k = 0; rest > 0; k = (k + 1) % byRemainder.length, rest--) out[byRemainder[k].i] += 1
  return out
}

// ——— Het grootboek van één bestelling ———

export type RevenueLine = {
  productId: string
  name: string
  qty: number
  unitPriceCents: number
  /** Zoals bewaard bij de verkoop. */
  vatRate: number
  giftCard: boolean
  /** Een behandeling (salon): telt als omzet, maar niet bij "best verkocht" (producten). */
  treatment?: boolean
  /** Een vooruitbetaalde kuur (webshop of kassa): omzet, apart van losse behandelingen. */
  kuur?: boolean
}

export type RevenueRefund = { at: string; amountCents: number; lines?: RefundLine[] }

/** Wat de omzetregels van een bestelling nodig hebben. */
export type RevenueOrder = {
  /** "YYYY-MM-DDTHH:mm". `null` = niet betaald: telt niet mee. */
  paidAt: string | null
  /** Waar het verkocht is: een webshopbestelling of een bon uit de kassa. */
  channel: SalesChannel
  paymentMethod: MoneyMethod
  lines: RevenueLine[]
  shippingCents: number
  shippingVatRate: number
  refunds: RevenueRefund[]
  /** Met een cadeaubon betaald, in centen (0 of leeg = geen). Telt als ontvangen "cadeaubon", niet als extra omzet. */
  voucherCents?: number
  /** Kassa, gesplitst betaald: dit deel kwam contant binnen, de rest via `paymentMethod` (pin). */
  cashPartCents?: number
  /**
   * Kassa: met klanttegoed betaald. Geen extra omzet; het tegoed (een schuld) gaat omlaag.
   * Negatief = als tegoed terugbetaald (gestopte kuur): het tegoed gaat omhoog.
   */
  creditCents?: number
  /**
   * Alleen een terugbetaling, zonder verkoop in dit overzicht (een gestopte kuur: de verkoop telde
   * al, misschien in de webshop of vóór de kassa er was). `lines` beschrijven wat er terugging; ze
   * tellen niet als verkoop en niet als bestelling.
   */
  refundOnly?: boolean
  /** Salon: wie de bon uitvoerde (`Sale.staffId`). Leeg = de eigenaar. Voor "Per uitvoerder". */
  performerId?: string
}

export type LedgerKind = 'product' | 'treatment' | 'kuur' | 'shipping' | 'giftcard'

/** Eén boekingsregel: een verkoop (positief) of een terugbetaling (negatief) op een dag. */
export type LedgerEntry = {
  date: string
  channel: SalesChannel
  kind: LedgerKind
  productId: string | null
  name: string
  /** Stuks; bij een terugbetaling negatief als de producten terugkomen, anders 0. */
  qty: number
  vatRate: number
  grossCents: number
  exVatCents: number
  method: MoneyMethod
  refund: boolean
  /** Alleen salon, alleen als bekend. */
  performerId?: string
}

type Part = { kind: LedgerKind; productId: string | null; name: string; qty: number; vatRate: number; gross: number }

function partsOf(o: Pick<RevenueOrder, 'lines' | 'shippingCents' | 'shippingVatRate'>): Part[] {
  const lines = o.lines.map<Part>((l) => ({
    kind: l.giftCard ? 'giftcard' : l.kuur ? 'kuur' : l.treatment ? 'treatment' : 'product',
    productId: l.productId,
    name: l.name,
    qty: l.qty,
    vatRate: l.giftCard ? GIFT_CARD_VAT_RATE : l.vatRate,
    gross: l.unitPriceCents * l.qty,
  }))
  return o.shippingCents > 0
    ? [...lines, { kind: 'shipping', productId: null, name: 'Verzending', qty: 1, vatRate: o.shippingVatRate, gross: o.shippingCents }]
    : lines
}

/** Over welke delen een terugbetaling valt, met het bedrag per deel (positief) en de teruggekomen stuks. */
function refundSplit(parts: Part[], r: RevenueRefund): { part: Part; gross: number; qty: number }[] {
  const chosen = (r.lines ?? []).flatMap((l) => {
    const part = parts.find((p) => p.kind !== 'shipping' && p.productId === l.productId)
    if (!part || part.qty === 0) return []
    const qty = Math.min(Math.max(0, l.qty), part.qty)
    return qty > 0 ? [{ part, max: Math.round((part.gross * qty) / part.qty), qty }] : []
  })
  const value = chosen.reduce((s, c) => s + c.max, 0)

  // Zonder regels: naar verhouding over de hele bestelling, er komt niets terug.
  if (chosen.length === 0) {
    const split = allocate(r.amountCents, parts.map((p) => p.gross))
    return parts.map((part, i) => ({ part, gross: split[i] ?? 0, qty: 0 }))
  }
  // Minder dan de regels waard zijn: naar verhouding over die regels (bijv. een deel van de prijs terug).
  if (r.amountCents <= value) {
    const split = allocate(r.amountCents, chosen.map((c) => c.max))
    return chosen.map((c, i) => ({ part: c.part, gross: split[i], qty: split[i] === c.max ? c.qty : 0 }))
  }
  // Meer: de regels helemaal, de rest op de verzendkosten (nooit meer dan die waren), wat dan nog
  // over is naar verhouding. `refundProblem` begrenst het totaal al; dit houdt de grootboekregels
  // ook zonder die controle kloppend.
  const rest = r.amountCents - value
  const shipping = parts.find((p) => p.kind === 'shipping')
  const onShipping = shipping ? Math.min(rest, shipping.gross) : 0
  const others = [
    ...(shipping && onShipping > 0 ? [{ part: shipping, gross: onShipping, qty: 0 }] : []),
    ...(rest > onShipping ? spread(parts, rest - onShipping) : []),
  ]
  return [...chosen.map((c) => ({ part: c.part, gross: c.max, qty: c.qty })), ...others]
}

/** Wat een creditnota nodig heeft van de bestelling: de regels zoals verkocht en de verzending. */
export type CreditSource = {
  lines: (Pick<RevenueLine, 'productId' | 'name' | 'qty' | 'unitPriceCents' | 'vatRate'> & { giftCard?: boolean })[]
  shippingCents: number
  shippingVatRate: number
}

/**
 * Btw per tarief van één terugbetaling, met precies dezelfde verdeling als het grootboek
 * (`refundSplit`): voor de creditnota. Som van `grossCents` = het terugbetaalde bedrag.
 */
export function refundVatByRate(o: CreditSource, r: RevenueRefund): { vatRate: number; grossCents: number; vatCents: number }[] {
  const parts = partsOf({ ...o, lines: o.lines.map((l) => ({ ...l, giftCard: l.giftCard ?? false })) })
  const byRate = new Map<number, number>()
  refundSplit(parts, r).forEach(({ part, gross }) => byRate.set(part.vatRate, (byRate.get(part.vatRate) ?? 0) + gross))
  return [...byRate]
    .filter(([, gross]) => gross > 0)
    .sort(([a], [b]) => b - a)
    .map(([vatRate, grossCents]) => ({ vatRate, grossCents, vatCents: grossCents - exVat(grossCents, vatRate) }))
}

const spread = (parts: Part[], amount: number) => {
  const split = allocate(amount, parts.map((p) => p.gross))
  return parts.map((part, i) => ({ part, gross: split[i] ?? 0, qty: 0 }))
}

/** Alle boekingsregels van één bestelling: de verkoop op de betaaldag, elke terugbetaling op haar eigen dag. */
export function orderLedger(o: RevenueOrder): LedgerEntry[] {
  if (!o.paidAt) return []
  const paidDay = o.paidAt.slice(0, 10)
  const parts = partsOf(o)
  const entry = (date: string, part: Part, gross: number, qty: number, refund: boolean): LedgerEntry => ({
    date,
    channel: o.channel,
    kind: part.kind,
    productId: part.productId,
    name: part.name,
    qty,
    vatRate: part.vatRate,
    grossCents: gross,
    exVatCents: exVat(gross, part.vatRate),
    method: o.paymentMethod,
    refund,
    ...(o.performerId ? { performerId: o.performerId } : {}),
  })
  const sale = o.refundOnly ? [] : parts.map((p) => entry(paidDay, p, p.gross, p.kind === 'shipping' ? 0 : p.qty, false))
  const refunds = o.refunds.flatMap((r) =>
    refundSplit(parts, r)
      .filter((s) => s.gross > 0)
      .map((s) => entry(r.at.slice(0, 10), s.part, -s.gross, -s.qty, true)),
  )
  return [...sale, ...refunds]
}

// ——— Rapport ———

/**
 * De vorige periode, zodat "tot nu toe" eerlijk vergeleken wordt:
 * - begint op de 1e van een maand, kwartaal of jaar en valt er binnen → dezelfde dagen één
 *   maand, kwartaal of jaar terug (1–3 okt → 1–3 sep; 1 jan–3 okt → 1 jan–3 okt vorig jaar).
 *   De kleinste passende eenheid wint; het scherm toont de vergelijkingsdatums er altijd bij.
 * - meerdere hele maanden → evenveel maanden terug;
 * - anders even lang, direct ervoor.
 */
export function previousRange(from: string, to: string): { from: string; to: string } {
  if (from.endsWith('-01')) {
    const units: { months: number; aligned: boolean }[] = [
      { months: 1, aligned: true },
      { months: 3, aligned: quarterStart(from) === from },
      { months: 12, aligned: from.slice(5) === '01-01' },
    ]
    const unit = units.find((u) => u.aligned && to < shiftMonths(from, u.months))
    if (unit) return { from: shiftMonths(from, -unit.months), to: shiftMonths(to, -unit.months) }
    const index = (d: string) => Number(d.slice(0, 4)) * 12 + Number(d.slice(5, 7))
    const months = index(to) - index(from) + 1
    return { from: shiftMonths(from, -months), to: shiftMonths(to, -months) }
  }
  const days = daysBetween(from, to) + 1
  return { from: shiftDate(from, -days), to: shiftDate(from, -1) }
}

/** Het begin van het vak waarin een dag valt. */
export const bucketOf = (date: string, groupBy: SalesGroupBy) =>
  groupBy === 'day' ? date : groupBy === 'week' ? iso(startOfWeek(fromIso(date))) : monthStart(date)

/** Een redelijke indeling voor een periode: per dag tot een maand, per week tot een kwartaal, daarna per maand. */
export const groupByFor = (from: string, to: string): SalesGroupBy => {
  const days = daysBetween(from, to) + 1
  return days <= 31 ? 'day' : days <= 92 ? 'week' : 'month'
}

const sum = <T,>(items: T[], pick: (t: T) => number) => items.reduce((s, t) => s + pick(t), 0)
const within = (date: string, from: string, to: string) => date >= from && date <= to

/** Een deel van wat er via `method` geboekt staat, kwam eigenlijk via `to` binnen (cadeaubon, of contant bij gesplitst). */
type VoucherUse = { date: string; channel: SalesChannel; method: MoneyMethod; to: ReceivedMethod; cents: number }

/** Per betaalde bestelling: op de betaaldag het cadeaubondeel naar "cadeaubon" en een contant deel naar "contant". */
const voucherUses = (orders: RevenueOrder[]): VoucherUse[] =>
  orders.flatMap((o) => {
    if (!o.paidAt) return []
    const base = { date: o.paidAt.slice(0, 10), channel: o.channel, method: o.paymentMethod }
    return [
      ...(o.voucherCents ? [{ ...base, to: 'cadeaubon' as const, cents: o.voucherCents }] : []),
      ...(o.cashPartCents ? [{ ...base, to: 'contant' as const, cents: o.cashPartCents }] : []),
      ...(o.creditCents ? [{ ...base, to: 'tegoed' as const, cents: o.creditCents }] : []),
    ]
  })

/** Wat er per manier binnenkwam (terugbetalingen eraf), met ingewisselde bonnen en contante delen apart. */
function receivedBy(entries: LedgerEntry[], uses: VoucherUse[]): Map<ReceivedMethod, number> {
  const by = new Map<ReceivedMethod, number>()
  const add = (m: ReceivedMethod, cents: number) => by.set(m, (by.get(m) ?? 0) + cents)
  entries.forEach((e) => add(e.method, e.grossCents))
  uses.forEach((u) => {
    add(u.method, -u.cents)
    add(u.to, u.cents)
  })
  return by
}

function totalsFor(orders: RevenueOrder[], entries: LedgerEntry[], from: string, to: string): SalesTotals {
  const inRange = entries.filter((e) => within(e.date, from, to))
  const revenue = inRange.filter((e) => e.kind !== 'giftcard')
  const paid = orders.filter((o) => !o.refundOnly && o.paidAt && within(o.paidAt.slice(0, 10), from, to))
  const paidGross = sum(paid, (o) => sum(o.lines, (l) => l.unitPriceCents * l.qty) + o.shippingCents)
  return {
    revenueExVatCents: sum(revenue, (e) => e.exVatCents),
    vatCents: sum(revenue, (e) => e.grossCents - e.exVatCents),
    refundsCents: -sum(inRange.filter((e) => e.refund), (e) => e.grossCents),
    orders: paid.length,
    averageOrderCents: paid.length ? Math.round(paidGross / paid.length) : 0,
    shippingExVatCents: sum(inRange.filter((e) => e.kind === 'shipping'), (e) => e.exVatCents),
    giftCardsCents: sum(inRange.filter((e) => e.kind === 'giftcard'), (e) => e.grossCents),
    treatmentsExVatCents: sum(inRange.filter((e) => e.kind === 'treatment' || e.kind === 'kuur'), (e) => e.exVatCents),
  }
}

export const salesChannels: readonly SalesChannel[] = ['webshop', 'salon']
export const channelLabel: Record<SalesChannel, string> = { webshop: 'Webshop', salon: 'Salon' }

const exVatOf = (list: LedgerEntry[], kind: LedgerKind) => sum(list.filter((e) => e.kind === kind), (e) => e.exVatCents)

/** Omzet per kanaal, uitgesplitst. Telt samen op tot `totals.revenueExVatCents` van alle kanalen. */
function channelBreakdown(orders: RevenueOrder[], inRange: LedgerEntry[], from: string, to: string): ChannelBreakdown[] {
  return salesChannels.map((channel) => {
    const list = inRange.filter((e) => e.channel === channel)
    return {
      channel,
      sales: orders.filter((o) => !o.refundOnly && o.channel === channel && o.paidAt && within(o.paidAt.slice(0, 10), from, to)).length,
      revenueExVatCents: sum(list.filter((e) => e.kind !== 'giftcard'), (e) => e.exVatCents),
      productsExVatCents: exVatOf(list, 'product'),
      treatmentsExVatCents: exVatOf(list, 'treatment'),
      kurenExVatCents: exVatOf(list, 'kuur'),
      shippingExVatCents: exVatOf(list, 'shipping'),
    }
  })
}

/**
 * Salonomzet per uitvoerder (wie de bon uitvoerde), excl. btw, zonder cadeaubonnen. Een regel
 * zonder uitvoerder hoort bij de eigenaar. Samen precies de salonomzet van `byChannel`.
 * Webshopomzet heeft geen uitvoerder en telt hier niet.
 */
export function performerBreakdown(inRange: readonly LedgerEntry[]): PerformerBreakdown[] {
  const by = new Map<string, PerformerBreakdown>()
  inRange
    .filter((e) => e.channel === 'salon' && e.kind !== 'giftcard')
    .forEach((e) => {
      const staffId = e.performerId || OWNER_STAFF_ID
      const row = by.get(staffId) ?? { staffId, revenueExVatCents: 0, treatmentsExVatCents: 0, productsExVatCents: 0 }
      by.set(staffId, {
        staffId,
        revenueExVatCents: row.revenueExVatCents + e.exVatCents,
        treatmentsExVatCents: row.treatmentsExVatCents + (e.kind === 'treatment' || e.kind === 'kuur' ? e.exVatCents : 0),
        productsExVatCents: row.productsExVatCents + (e.kind === 'product' ? e.exVatCents : 0),
      })
    })
  return [...by.values()].sort((a, b) => b.revenueExVatCents - a.revenueExVatCents)
}

/**
 * GET /admin/reports/sales — precies zo rekent de server het ook. Met `channel` gaat alles over dat
 * kanaal, behalve de btw per tarief en de splitsing per kanaal: die gaan altijd over alles samen
 * (de btw-aangifte kent geen kanalen).
 */
export function salesReport(all: RevenueOrder[], from: string, to: string, groupBy: SalesGroupBy, channel?: SalesChannel): SalesReport {
  const orders = channel ? all.filter((o) => o.channel === channel) : all
  const allInRange = all.flatMap(orderLedger).filter((e) => within(e.date, from, to))
  const entries = orders.flatMap(orderLedger)
  const prev = previousRange(from, to)
  const inRange = entries.filter((e) => within(e.date, from, to))
  const revenue = inRange.filter((e) => e.kind !== 'giftcard')

  const series = new Map<string, number>()
  for (let d = from; d <= to; d = shiftDate(d, 1)) {
    const key = bucketOf(d, groupBy)
    if (!series.has(key)) series.set(key, 0)
  }
  revenue.forEach((e) => {
    const key = bucketOf(e.date, groupBy)
    series.set(key, (series.get(key) ?? 0) + e.exVatCents)
  })

  const byRate = new Map<number, { revenueExVatCents: number; vatCents: number }>()
  allInRange.filter((e) => e.kind !== 'giftcard').forEach((e) => {
    const r = byRate.get(e.vatRate) ?? { revenueExVatCents: 0, vatCents: 0 }
    byRate.set(e.vatRate, { revenueExVatCents: r.revenueExVatCents + e.exVatCents, vatCents: r.vatCents + e.grossCents - e.exVatCents })
  })

  const byMethod = receivedBy(
    inRange,
    voucherUses(orders).filter((u) => within(u.date, from, to)),
  )

  const top = new Map<string, { productId: string; name: string; qty: number; revenueExVatCents: number }>()
  inRange.forEach((e) => {
    const id = e.productId
    if (e.kind !== 'product' || !id) return
    const t = top.get(id) ?? { productId: id, name: e.name, qty: 0, revenueExVatCents: 0 }
    top.set(id, { ...t, qty: t.qty + e.qty, revenueExVatCents: t.revenueExVatCents + e.exVatCents })
  })

  return {
    totals: totalsFor(orders, entries, from, to),
    previous: totalsFor(orders, entries, prev.from, prev.to),
    series: [...series].map(([date, revenueExVatCents]) => ({ date, revenueExVatCents })),
    byVatRate: [...byRate].map(([vatRate, v]) => ({ vatRate, ...v })).sort((a, b) => b.vatRate - a.vatRate),
    byChannel: channelBreakdown(all, allInRange, from, to),
    byPerformer: performerBreakdown(allInRange),
    byPaymentMethod: [...byMethod]
      .filter(([, amountCents]) => amountCents !== 0)
      .map(([method, amountCents]) => ({ method, amountCents }))
      .sort((a, b) => b.amountCents - a.amountCents),
    topProducts: [...top.values()]
      .filter((t) => t.revenueExVatCents > 0)
      .sort((a, b) => b.revenueExVatCents - a.revenueExVatCents)
      .slice(0, 10),
  }
}

// ——— Export voor de boekhouder (Exact Online) ———

export const paymentMethodLabel: Record<PaymentMethod, string> = {
  ideal: 'Wero',
  bancontact: 'Bancontact',
  klarna: 'Klarna',
  card: 'Creditcard',
}

/** Betaalmethoden (webshop en salon) plus de ingewisselde cadeaubon (Omzet en de export). */
export const receivedMethodLabel: Record<ReceivedMethod, string> = { ...paymentMethodLabel, pin: 'Pin (salon)', contant: 'Contant (salon)', cadeaubon: 'Cadeaubon', tegoed: 'Klanttegoed' }

/** 1234 → "12,34" (Nederlandse Excel en Exact lezen een komma). */
const csvMoney = (cents: number) => `${cents < 0 ? '-' : ''}${Math.floor(Math.abs(cents) / 100)},${String(Math.abs(cents) % 100).padStart(2, '0')}`

/**
 * GET /admin/reports/export.csv — één regel per dag per kanaal met omzet per btw-tarief,
 * verzending, cadeaubonnen, terugbetalingen en wat er per betaalmethode binnenkwam. Onderaan een
 * totaal per kanaal en een totaal over alles. Puntkomma's, komma als decimaalteken.
 */
export function salesCsv(orders: RevenueOrder[], from: string, to: string): string {
  const entries = orders.flatMap(orderLedger).filter((e) => within(e.date, from, to))
  const uses = voucherUses(orders).filter((u) => within(u.date, from, to))
  const rates = [...new Set(entries.filter((e) => e.kind !== 'giftcard').map((e) => e.vatRate))].sort((a, b) => b - a)
  const all = receivedBy(entries, uses)
  const methods = (Object.keys(receivedMethodLabel) as ReceivedMethod[]).filter((m) => all.has(m))
  const header = [
    'Datum',
    'Kanaal',
    ...rates.flatMap((r) => [`Omzet ${r}% excl. btw`, `Btw ${r}%`]),
    'Waarvan verzending excl. btw',
    'Cadeaubonnen verkocht',
    'Terugbetaald incl. btw',
    ...methods.map((m) => `Ontvangen ${receivedMethodLabel[m]}`),
  ]
  const row = (label: string, channel: string, list: LedgerEntry[], dayUses: VoucherUse[]) => {
    const revenue = list.filter((e) => e.kind !== 'giftcard')
    const received = receivedBy(list, dayUses)
    return [
      label,
      channel,
      ...rates.flatMap((r) => {
        const at = revenue.filter((e) => e.vatRate === r)
        return [csvMoney(sum(at, (e) => e.exVatCents)), csvMoney(sum(at, (e) => e.grossCents - e.exVatCents))]
      }),
      csvMoney(sum(list.filter((e) => e.kind === 'shipping'), (e) => e.exVatCents)),
      csvMoney(sum(list.filter((e) => e.kind === 'giftcard'), (e) => e.grossCents)),
      csvMoney(-sum(list.filter((e) => e.refund), (e) => e.grossCents)),
      ...methods.map((m) => csvMoney(received.get(m) ?? 0)),
    ].join(';')
  }
  const days = [...new Set(entries.map((e) => e.date))].sort()
  const of = (c: SalesChannel) => ({ e: entries.filter((x) => x.channel === c), u: uses.filter((x) => x.channel === c) })
  return [
    header.join(';'),
    ...days.flatMap((d) =>
      salesChannels.flatMap((c) => {
        const { e, u } = of(c)
        const day = e.filter((x) => x.date === d)
        return day.length ? [row(d, channelLabel[c], day, u.filter((x) => x.date === d))] : []
      }),
    ),
    ...salesChannels.flatMap((c) => {
      const { e, u } = of(c)
      return e.length ? [row('Totaal', channelLabel[c], e, u)] : []
    }),
    row('Totaal', 'Alle', entries, uses),
  ].join('\r\n')
}

// ——— Terugbetalen en annuleren ———

type OrderState = {
  paymentStatus: PaymentStatus
  fulfilment: OrderFulfilment
  totalCents: number
  refundedCents: number
  cancelledAt: string | null
  /** Met een cadeaubon betaald: dat deel gaat niet via Mollie terug. */
  voucherCents?: number
}

/** Wat er nog via Mollie terug kan: het betaalde bedrag, zonder het deel van de cadeaubon. */
export const refundableCents = (o: Pick<OrderState, 'totalCents' | 'refundedCents' | 'voucherCents'>) =>
  Math.max(0, o.totalCents - (o.voucherCents ?? 0) - o.refundedCents)

export type OrderProblem = { status: 409 | 422; code?: string; field?: 'amountCents' | 'reason'; message: string }

/** Waarom deze terugbetaling niet kan, of `null`. Zelfde regel in het scherm, de demo en de server. */
export function refundProblem(o: OrderState, amountCents: number, reason: string): OrderProblem | null {
  if (o.paymentStatus !== 'betaald') return { status: 422, message: 'Deze bestelling is niet betaald; er valt niets terug te betalen.' }
  if (!reason.trim()) return { status: 422, field: 'reason', message: 'Vul een reden in. Die staat bij de terugbetaling en in de boekhouding.' }
  if (!Number.isInteger(amountCents) || amountCents <= 0) return { status: 422, field: 'amountCents', message: 'Vul een bedrag in van minstens € 0,01.' }
  if (amountCents > refundableCents(o)) {
    return { status: 409, code: 'already_refunded', field: 'amountCents', message: 'Dat is meer dan er nog terug te betalen is.' }
  }
  return null
}

/** Waarom deze bestelling niet geannuleerd kan worden, of `null`. Betaald en nog 'nieuw' = annuleren én terugbetalen. */
export function cancelProblem(o: OrderState): OrderProblem | null {
  if (o.cancelledAt) return { status: 409, code: 'cannot_cancel', message: 'Deze bestelling is al geannuleerd.' }
  if (o.paymentStatus === 'betaald' && o.fulfilment !== 'nieuw') {
    return { status: 409, code: 'cannot_cancel', message: 'Deze bestelling is al ingepakt of verzonden. Betaal hem terug in plaats van te annuleren.' }
  }
  if (o.paymentStatus === 'betaald' && o.refundedCents > 0) {
    return { status: 409, code: 'cannot_cancel', message: 'Er is al een deel terugbetaald. Betaal de rest terug in plaats van te annuleren.' }
  }
  return null
}
